import React from 'react';
import type { AnimationProgressAPI, AnimationProgressProps } from '../types';

export const AnimationProgress: React.FC<AnimationProgressProps> =
  React.forwardRef<AnimationProgressAPI, AnimationProgressProps>(
    ({ strokeWidth, width, direction = 'cw', angle = 0, track }, ref) => {
      const [circle, setCircle] = React.useState<SVGCircleElement | null>(null);
      const [isPlaying, setIsPlaying] = React.useState(false);

      React.useImperativeHandle(
        ref,
        () => ({
          play: (duration: number) => {
            if (circle) {
              setIsPlaying(true);
              circle.animate(
                [
                  { strokeDasharray: '1 1', strokeDashoffset: 0 },
                  {
                    strokeDasharray: '1 1',
                    strokeDashoffset: direction === 'cw' ? -1 : 1,
                  },
                ],
                {
                  duration,
                  fill: 'forwards',
                  easing: 'linear',
                },
              ).onfinish = () => {
                setIsPlaying(false);
              };
              circle.animate(
                [
                  { strokeDasharray: 'unset', opacity: 0 },
                  { strokeDasharray: 'unset', opacity: 1 },
                ],
                {
                  delay: duration + 100,
                  duration: 300,
                  fill: 'forwards',
                },
              );
            }
          },
          stop: () => {
            if (circle) {
              circle.animate(
                [{ strokeDasharray: 'unset', strokeDashoffset: 'unset' }],
                {
                  duration: 100,
                  fill: 'forwards',
                  easing: 'ease-out',
                },
              ).onfinish = () => {
                circle
                  .getAnimations()
                  .forEach((animation) => animation.cancel());
                setIsPlaying(false);
              };
            }
          },
          isPlaying,
        }),
        [circle, direction, isPlaying],
      );

      return (
        <svg
          width={width}
          height={width}
          viewBox={`0 0 ${width} ${width}`}
          style={{ padding: strokeWidth / 2, overflow: 'visible' }}
        >
          {track && (
            <circle
              cx={width / 2}
              cy={width / 2}
              r={width / 2}
              fill="none"
              stroke={track.color ? track.color : 'currentColor'}
              strokeOpacity={track.opacity}
              strokeWidth={Math.min(track.strokeWidth, strokeWidth)}
            />
          )}
          <circle
            ref={setCircle}
            cx={width / 2}
            cy={width / 2}
            r={width / 2}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            transform={`rotate(${angle} ${width / 2} ${width / 2})`}
            pathLength="1"
          />
        </svg>
      );
    },
  );
